import { useState, useEffect, useMemo } from "react";
import { Plus, Pencil, Trash2, Search, Loader2, X, FileDown, CalendarPlus, CheckCircle2, Clock, Users, Star, Sheet, FileText } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AdminLayout } from "@/components/AdminLayout";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { childrenAPI, workshopsAPI, bookingsAPI } from "@/lib/api";
import { toast } from "sonner";
import { format, parse, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const initialFormData = {
  full_name: "",
  date_of_birth: "",
  phone: "",
  school_grade: "",
  guardian_phone: "",
  guardian_name: "",
  school_name: "",
  study_shift: "",
};

const studyShifts = [
  { value: "manha", label: "Manhã" },
  { value: "tarde", label: "Tarde" },
  { value: "noite", label: "Noite" },
  { value: "integral", label: "Integral" },
];

const schoolGrades = [
  "1º Ano", "2º Ano", "3º Ano", "4º Ano", "5º Ano",
  "6º Ano", "7º Ano", "8º Ano", "9º Ano",
  "1º Médio", "2º Médio", "3º Médio"
];

// ------------------------------------------------------------------
// 1. Normaliza: remove acentos, prefixos comuns, pontução, lowercase.
//    "Colégio Propósito" e "ColProprosito" viram "proposito".
// 2. levenshtein: distância de edição entre duas strings.
//    Usado para agrupar typos como "Proprosito" (extra 'r') com "Proposito".
// ------------------------------------------------------------------
const normalizeSchool = (name = "") =>
  name
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // remove acentos
    .toLowerCase()
    .replace(/col[eé]gio\.?\s*/g, "")     // remove "colégio" / "col."
    .replace(/instituto\.?\s*/g, "")       // remove "instituto" / "inst."
    .replace(/escola\.?\s*/g, "")          // remove "escola"
    .replace(/[^a-z0-9\s]/g, "")           // remove pontuação restante
    .replace(/\s+/g, " ")                  // múltiplos espaços -> 1
    .trim();

const levenshtein = (a, b) => {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  // Cria matriz (m+1) x (n+1) inicializada com 0..n e 0..m
  const dp = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
};

// Threshold: agrupa se dist ≤2 E ambas as chaves têm >= 6 chars.
// Evita mesclar acrônimos curtos como "IDB" com "IDA".
const fuzzyThreshold = (a, b) =>
  Math.min(a.length, b.length) >= 6 && levenshtein(a, b) <= 2;

// ------------------------------------------------------------------
// Aliases manuais: siglas, abreviações ou nomes alternativos conhecidos.
// Cada array é um grupo equivalente — o 1º elemento é a chave canônica.
// Os valores são chaves JÁ NORMALIZADAS (resultado de normalizeSchool).
// Adicione novos grupos aqui conforme necessário.
// ------------------------------------------------------------------
const SCHOOL_ALIASES = [
  ["dom barreto", "idb"],
  // Exemplo de como adicionar mais:
  // ["nome canônico normalizado", "sigla", "outro alias"],
];

// Monta o lookup: normalizedKey -> canonicalKey
const ALIAS_LOOKUP = {};
SCHOOL_ALIASES.forEach(group => {
  const canonical = group[0];
  group.forEach(alias => { ALIAS_LOOKUP[alias] = canonical; });
});

// Aplica aliases após normalização
const resolveSchoolKey = (normalized) => ALIAS_LOOKUP[normalized] ?? normalized;

export default function ChildrenPage() {
  const [children, setChildren] = useState([]);
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [birthDate, setBirthDate] = useState(null);
  const [birthDateInput, setBirthDateInput] = useState("");

  // Booking by admin state
  const [bookingMap, setBookingMap] = useState({}); // child_id -> booking | null
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [bookingChild, setBookingChild] = useState(null);
  const [availableWorkshops, setAvailableWorkshops] = useState([]);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [isLoadingWorkshops, setIsLoadingWorkshops] = useState(false);
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);

  // Agrupa colégios similares usando normalização + Levenshtein.
  // Retorna { schoolOptions, normalizedToCanon } onde:
  //   schoolOptions  = [{ key, label (mais frequente), count }]
  //   normalizedToCanon = { normalizedKey -> canonicalGroupKey }
  const { schoolOptions, normalizedToCanon } = useMemo(() => {
    // 1) Contar frequência por chave normalizada
    const freq = {}; // normalizedKey -> { rawName -> count }
    children.forEach(c => {
      const raw = (c.school_name || "").trim();
      if (!raw) return;
      const key = resolveSchoolKey(normalizeSchool(raw));
      if (!freq[key]) freq[key] = {};
      freq[key][raw] = (freq[key][raw] || 0) + 1;
    });

    // 2) Mesclar grupos com chaves fuzzy-similares (typos)
    const allKeys = Object.keys(freq);
    const canon = {}; // normalizedKey -> canonicalKey (representação do grupo)
    const merged = {}; // canonicalKey -> { rawName -> count }

    allKeys.forEach(key => {
      if (canon[key]) return; // já atribuído a um grupo
      // Verifica se já existe um grupo canonônico parecido
      const existingCanon = Object.keys(merged).find(c => fuzzyThreshold(c, key));
      if (existingCanon) {
        canon[key] = existingCanon;
        Object.entries(freq[key]).forEach(([raw, cnt]) => {
          merged[existingCanon][raw] = (merged[existingCanon][raw] || 0) + cnt;
        });
      } else {
        canon[key] = key;
        merged[key] = { ...freq[key] };
      }
    });

    // 3) Montar schoolOptions
    const options = Object.entries(merged)
      .map(([key, labels]) => {
        const canonical = Object.entries(labels).sort((a, b) => b[1] - a[1])[0][0];
        const count = Object.values(labels).reduce((s, n) => s + n, 0);
        return { key, label: canonical, count };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }));

    return { schoolOptions: options, normalizedToCanon: canon };
  }, [children]);

  useEffect(() => {
    loadChildren();
  }, []);

  // Reload booking statuses whenever the children list changes
  useEffect(() => {
    if (children.length > 0) loadBookingMap();
  }, [children]);

  useEffect(() => {
    const filtered = children.filter(child => {
      const matchSearch =
        child.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        child.school_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        child.guardian_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchSchool =
        schoolFilter === "all" ||
        normalizedToCanon[resolveSchoolKey(normalizeSchool(child.school_name))] === schoolFilter;
      return matchSearch && matchSchool;
    });
    setFilteredChildren(filtered);
  }, [searchTerm, schoolFilter, children]);

  const loadChildren = async () => {
    try {
      const response = await childrenAPI.list();
      setChildren(response.data);
      setFilteredChildren(response.data);
    } catch (error) {
      toast.error("Erro ao carregar crianças");
    } finally {
      setIsLoading(false);
    }
  };

  const loadBookingMap = async () => {
    try {
      const response = await bookingsAPI.list();
      const map = {};
      for (const booking of response.data) {
        map[booking.child_id] = booking;
      }
      setBookingMap(map);
    } catch {
      // silently ignore
    }
  };

  const handleOpenBookingDialog = async (child) => {
    setBookingChild(child);
    setSelectedWorkshop(null);
    setIsBookingDialogOpen(true);
    setIsLoadingWorkshops(true);
    try {
      const res = await workshopsAPI.list({ available_only: true });
      setAvailableWorkshops(res.data.filter(w => w.available_slots > 0));
    } catch {
      toast.error("Erro ao carregar workshops");
    } finally {
      setIsLoadingWorkshops(false);
    }
  };

  const handleAdminBooking = async () => {
    if (!bookingChild || !selectedWorkshop) return;
    setIsCreatingBooking(true);
    try {
      await bookingsAPI.create({
        child_id: bookingChild.id,
        workshop_id: selectedWorkshop.id,
      });
      toast.success(`✅ Agendamento de ${bookingChild.full_name} realizado com sucesso!`);
      setIsBookingDialogOpen(false);
      setBookingChild(null);
      setSelectedWorkshop(null);
      await loadBookingMap();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao realizar agendamento";
      toast.error(msg);
    } finally {
      setIsCreatingBooking(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parse(dateStr, "yyyy-MM-dd", new Date()), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatWeekday = (dateStr) => {
    try {
      return format(parse(dateStr, "yyyy-MM-dd", new Date()), "EEEE", { locale: ptBR });
    } catch {
      return "";
    }
  };

  const handleOpenDialog = (child = null) => {
    if (child) {
      setSelectedChild(child);
      setFormData({
        full_name: child.full_name,
        date_of_birth: child.date_of_birth,
        phone: child.phone,
        school_grade: child.school_grade,
        guardian_phone: child.guardian_phone,
        guardian_name: child.guardian_name,
        school_name: child.school_name,
        study_shift: child.study_shift,
      });
      const parsedDate = parse(child.date_of_birth, "yyyy-MM-dd", new Date());
      setBirthDate(parsedDate);
      setBirthDateInput(format(parsedDate, "dd/MM/yyyy"));
    } else {
      setSelectedChild(null);
      setFormData(initialFormData);
      setBirthDate(null);
      setBirthDateInput("");
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedChild(null);
    setFormData(initialFormData);
    setBirthDate(null);
    setBirthDateInput("");
  };

  const handleDateInputChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 8) value = value.slice(0, 8);

    if (value.length >= 5) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
    } else if (value.length >= 3) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    }
    setBirthDateInput(value);

    if (value.length === 10) {
      const parsedDate = parse(value, "dd/MM/yyyy", new Date());
      if (isValid(parsedDate)) {
        setFormData({ ...formData, date_of_birth: format(parsedDate, "yyyy-MM-dd") });
        setBirthDate(parsedDate);
      } else {
        setFormData({ ...formData, date_of_birth: "" });
        setBirthDate(null);
      }
    } else {
      setFormData({ ...formData, date_of_birth: "" });
      setBirthDate(null);
    }
  };

  const calculateAge = (birthDateStr) => {
    if (!birthDateStr) return "";
    const today = new Date();
    const birth = parse(birthDateStr, "yyyy-MM-dd", new Date());
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    if (!formData.date_of_birth) {
      toast.error("Data de nascimento inválida ou incompleta.");
      setIsSaving(false);
      return;
    }

    try {
      if (selectedChild) {
        await childrenAPI.update(selectedChild.id, formData);
        toast.success("Criança atualizada com sucesso!");
      } else {
        await childrenAPI.create(formData);
        toast.success("Criança cadastrada com sucesso!");
      }
      handleCloseDialog();
      loadChildren();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao salvar";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedChild) return;

    try {
      await childrenAPI.delete(selectedChild.id);
      toast.success("Criança removida com sucesso!");
      setIsDeleteDialogOpen(false);
      setSelectedChild(null);
      loadChildren();
    } catch (error) {
      toast.error("Erro ao remover criança");
    }
  };

  const openDeleteDialog = (child) => {
    setSelectedChild(child);
    setIsDeleteDialogOpen(true);
  };

  const buildExportRows = () => {
    const studyShiftLabel = { manha: "Manhã", tarde: "Tarde", noite: "Noite", integral: "Integral" };
    return filteredChildren.map((child, idx) => ({
      "#": idx + 1,
      "Nome Completo": child.full_name || "",
      "Nascimento": child.date_of_birth ? child.date_of_birth.split("-").reverse().join("/") : "",
      "Idade": child.age != null ? `${child.age} anos` : "",
      "Colégio": child.school_name || "",
      "Série": child.school_grade || "",
      "Turno": studyShiftLabel[child.study_shift] || child.study_shift || "",
      "Tel. Criança": child.phone || "",
      "Nome Responsável": child.guardian_name || "",
      "Tel. Responsável": child.guardian_phone || "",
      "Cadastrado em": child.created_at ? new Date(child.created_at).toLocaleDateString("pt-BR") : "",
    }));
  };

  const buildFileSlug = () => {
    const now = new Date();
    const datePart = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
    const schoolPart = schoolFilter !== "all"
      ? `_${schoolFilter.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "")}`
      : "";
    return `leads_javis${schoolPart}_${datePart}`;
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    // Header
    doc.setFillColor(109, 40, 217);
    doc.rect(0, 0, 297, 20, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    const schoolLabel = schoolFilter !== "all" ? ` — ${schoolFilter}` : "";
    doc.text(`Relatório de Leads — Javis Academy${schoolLabel}`, 14, 13);

    const now = new Date();
    const dateStr = now.toLocaleDateString("pt-BR") + " às " + now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Gerado em: ${dateStr}  |  Total: ${filteredChildren.length} cadastro(s)`, 14, 18);

    const rows = buildExportRows().map(r => Object.values(r));

    autoTable(doc, {
      startY: 24,
      head: [["#", "Nome Completo", "Nascimento", "Idade", "Colégio", "Série", "Turno", "Tel. Criança", "Nome Responsável", "Tel. Responsável", "Cadastrado em"]],
      body: rows,
      styles: { fontSize: 7.5, cellPadding: 2.5, overflow: "linebreak" },
      headStyles: { fillColor: [109, 40, 217], textColor: 255, fontStyle: "bold", fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 243, 255] },
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 40 },
        2: { cellWidth: 22, halign: "center" },
        3: { cellWidth: 18, halign: "center" },
        4: { cellWidth: 35 },
        5: { cellWidth: 18, halign: "center" },
        6: { cellWidth: 16, halign: "center" },
        7: { cellWidth: 26 },
        8: { cellWidth: 35 },
        9: { cellWidth: 26 },
        10: { cellWidth: 22, halign: "center" },
      },
      margin: { left: 14, right: 14 },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(
          `Página ${data.pageNumber} de ${pageCount}`,
          data.settings.margin.left,
          doc.internal.pageSize.height - 6
        );
      },
    });

    doc.save(`${buildFileSlug()}.pdf`);
  };

  const exportToExcel = () => {
    const rows = buildExportRows();
    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto column widths
    const colWidths = Object.keys(rows[0] || {}).map(key => ({
      wch: Math.max(key.length, ...rows.map(r => String(r[key] || "").length)) + 2,
    }));
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    const sheetName = schoolFilter !== "all" ? schoolFilter.slice(0, 31) : "Leads";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${buildFileSlug()}.xlsx`);
  };

  const exportToCSV = () => {
    const rows = buildExportRows();
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;
    const csvContent = [
      headers.map(escape).join(","),
      ...rows.map(row => headers.map(h => escape(row[h])).join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${buildFileSlug()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="Gerenciar Crianças">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3">
          {/* Row 1: search + add */}
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, escola ou responsável..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-xl"
                data-testid="search-children-input"
              />
            </div>
            <Button
              onClick={() => handleOpenDialog()}
              className="rounded-full btn-animate"
              data-testid="add-child-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nova Criança
            </Button>
          </div>

          {/* Row 2: school filter + export buttons */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            {/* School filter */}
            <div className="flex items-center gap-2 flex-1 max-w-sm">
              <Select value={schoolFilter} onValueChange={setSchoolFilter}>
                <SelectTrigger className="h-10 rounded-xl" data-testid="school-filter-select">
                  <SelectValue placeholder="Filtrar por colégio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">🏫 Todos os colégios ({children.length})</SelectItem>
                  {schoolOptions.map(({ key, label, count }) => (
                    <SelectItem key={key} value={key}>{label} ({count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {schoolFilter !== "all" && (
                <button
                  onClick={() => setSchoolFilter("all")}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Limpar filtro"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Export count badge */}
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {filteredChildren.length} registro{filteredChildren.length !== 1 ? "s" : ""}
            </span>

            {/* Export buttons */}
            <div className="flex gap-2 sm:ml-auto">
              <Button
                onClick={exportToPDF}
                variant="outline"
                className="rounded-full gap-2 border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                data-testid="export-pdf-button"
                disabled={filteredChildren.length === 0}
              >
                <FileDown className="w-4 h-4" />
                PDF
              </Button>
              <Button
                onClick={exportToExcel}
                variant="outline"
                className="rounded-full gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                data-testid="export-excel-button"
                disabled={filteredChildren.length === 0}
              >
                <Sheet className="w-4 h-4" />
                Excel
              </Button>
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="rounded-full gap-2 border-sky-300 text-sky-700 hover:bg-sky-50 hover:text-sky-800"
                data-testid="export-csv-button"
                disabled={filteredChildren.length === 0}
              >
                <FileText className="w-4 h-4" />
                CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredChildren.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchTerm ? "Nenhuma criança encontrada" : "Nenhuma criança cadastrada"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Idade</TableHead>
                      <TableHead className="hidden md:table-cell">Escola</TableHead>
                      <TableHead className="hidden lg:table-cell">Série</TableHead>
                      <TableHead className="hidden lg:table-cell">Responsável</TableHead>
                      <TableHead>Agendamento</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredChildren.map((child) => {
                      const booking = bookingMap[child.id];
                      return (
                      <TableRow key={child.id} data-testid={`child-row-${child.id}`}>
                        <TableCell className="font-medium">{child.full_name}</TableCell>
                        <TableCell>{child.age} anos</TableCell>
                        <TableCell className="hidden md:table-cell">{child.school_name}</TableCell>
                        <TableCell className="hidden lg:table-cell">{child.school_grade}</TableCell>
                        <TableCell className="hidden lg:table-cell">{child.guardian_name}</TableCell>
                        <TableCell>
                          {booking ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {formatDate(booking.workshop_date)}
                            </span>
                          ) : (
                            <button
                              onClick={() => handleOpenBookingDialog(child)}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full hover:bg-amber-100 transition-colors"
                              data-testid={`schedule-child-${child.id}`}
                            >
                              <CalendarPlus className="w-3.5 h-3.5" />
                              Agendar
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(child)}
                              className="rounded-full"
                              data-testid={`edit-child-${child.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(child)}
                              className="rounded-full text-destructive hover:text-destructive"
                              data-testid={`delete-child-${child.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-nunito text-xl">
              {selectedChild ? "Editar Criança" : "Cadastrar Nova Criança"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="full_name">Nome Completo *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="h-11 rounded-xl"
                  data-testid="child-fullname-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Data de Nascimento *</Label>
                <Input
                  value={birthDateInput}
                  onChange={handleDateInputChange}
                  placeholder="DD/MM/AAAA"
                  maxLength={10}
                  className="h-11 rounded-xl"
                  data-testid="child-birthdate-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Idade</Label>
                <Input
                  value={formData.date_of_birth ? `${calculateAge(formData.date_of_birth)} anos` : ""}
                  disabled
                  className="h-11 rounded-xl bg-muted"
                  data-testid="child-age-display"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone da Criança</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  className="h-11 rounded-xl"
                  data-testid="child-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Série Escolar *</Label>
                <Select
                  value={formData.school_grade}
                  onValueChange={(value) => setFormData({ ...formData, school_grade: value })}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="child-grade-select">
                    <SelectValue placeholder="Selecione a série" />
                  </SelectTrigger>
                  <SelectContent>
                    {schoolGrades.map((grade) => (
                      <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="school_name">Nome do Colégio *</Label>
                <Input
                  id="school_name"
                  value={formData.school_name}
                  onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                  required
                  className="h-11 rounded-xl"
                  data-testid="child-school-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Turno *</Label>
                <Select
                  value={formData.study_shift}
                  onValueChange={(value) => setFormData({ ...formData, study_shift: value })}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="child-shift-select">
                    <SelectValue placeholder="Selecione o turno" />
                  </SelectTrigger>
                  <SelectContent>
                    {studyShifts.map((shift) => (
                      <SelectItem key={shift.value} value={shift.value}>{shift.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="guardian_name">Nome do Responsável *</Label>
                <Input
                  id="guardian_name"
                  value={formData.guardian_name}
                  onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                  required
                  className="h-11 rounded-xl"
                  data-testid="child-guardian-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="guardian_phone">Telefone do Responsável *</Label>
                <Input
                  id="guardian_phone"
                  value={formData.guardian_phone}
                  onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  required
                  className="h-11 rounded-xl"
                  data-testid="child-guardian-phone-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="rounded-full">
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving} className="rounded-full" data-testid="save-child-button">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedChild ? "Salvar" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== ADMIN BOOKING DIALOG ===== */}
      <Dialog open={isBookingDialogOpen} onOpenChange={setIsBookingDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-nunito flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-primary" />
              Agendar para {bookingChild?.full_name}
            </DialogTitle>
            <DialogDescription>
              Escolha um horário disponível para realizar o agendamento manualmente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {isLoadingWorkshops ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-7 h-7 animate-spin text-primary" />
              </div>
            ) : availableWorkshops.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground gap-2">
                <CalendarPlus className="w-8 h-8 opacity-40" />
                <p className="text-sm">Nenhum horário disponível no momento.</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {availableWorkshops.map((workshop) => (
                  <button
                    key={workshop.id}
                    onClick={() => setSelectedWorkshop(workshop)}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left",
                      selectedWorkshop?.id === workshop.id
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border hover:border-primary/50 hover:bg-muted/40"
                    )}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                      selectedWorkshop?.id === workshop.id ? "bg-primary/15" : "bg-muted"
                    )}>
                      <Star className={cn("w-5 h-5", selectedWorkshop?.id === workshop.id ? "text-primary" : "text-muted-foreground")} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold font-nunito capitalize">
                        {formatDate(workshop.date)} — {formatWeekday(workshop.date)}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {workshop.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {workshop.available_slots} vagas
                        </span>
                      </div>
                    </div>
                    {selectedWorkshop?.id === workshop.id && (
                      <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBookingDialogOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button
              onClick={handleAdminBooking}
              disabled={!selectedWorkshop || isCreatingBooking}
              className="rounded-full"
              data-testid="confirm-admin-booking-button"
            >
              {isCreatingBooking ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Agendando...</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirmar Agendamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-nunito">Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Tem certeza que deseja remover <strong>{selectedChild?.full_name}</strong>? Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-full" data-testid="confirm-delete-button">
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
